// A port of music_kit_1/Core/Utilities/TrackMatcher.swift: picks the right recording out
// of catalog search results. Search is the last resort when an Apple Music ID does not
// resolve, and taking the first result is what puts a karaoke take on air, so candidates
// are scored and a bad enough best match is refused.

export interface TrackCandidate {
  id: string;
  isrc: string | null;
  title: string;
  artist: string;
  duration: number;
  /** False when the storefront lists the song but will not play it. */
  isPlayable: boolean;
}

export interface WantedTrack {
  isrc: string | null;
  title: string;
  artist: string;
  duration: number;
}

/** Below this a match is worse than no track at all. */
const MINIMUM_ACCEPTABLE_SCORE = 0;

const DISQUALIFYING_MARKERS = [
  "karaoke",
  "tribute",
  "made famous by",
  "made popular by",
  "in the style of",
  "originally performed by",
  "cover version",
];

const SOFT_MARKERS = ["live", "remix", "instrumental", "acoustic", "sped up", "slowed", "demo"];

export function bestMatch(
  candidates: TrackCandidate[],
  wanted: WantedTrack,
): TrackCandidate | null {
  let best: { candidate: TrackCandidate; score: number } | null = null;
  for (const candidate of candidates) {
    if (!candidate.isPlayable) continue;
    const candidateScore = score(candidate, wanted);
    if (!best || candidateScore > best.score) best = { candidate, score: candidateScore };
  }
  return best && best.score >= MINIMUM_ACCEPTABLE_SCORE ? best.candidate : null;
}

export function score(candidate: TrackCandidate, wanted: WantedTrack): number {
  if (wanted.isrc && candidate.isrc && wanted.isrc.toLowerCase() === candidate.isrc.toLowerCase()) {
    return 1000;
  }
  return (
    artistScore(candidate.artist, wanted.artist) +
    titleScore(candidate.title, wanted.title) +
    durationScore(candidate.duration, wanted.duration) +
    variantScore(candidate, wanted)
  );
}

function artistScore(candidate: string, wanted: string): number {
  if (!wanted) return 0;
  const a = normalized(candidate);
  const b = normalized(wanted);
  if (a === b) return 120;
  if (a && b && (a.includes(b) || b.includes(a))) return 60;
  return -250;
}

function titleScore(candidate: string, wanted: string): number {
  if (!wanted) return 0;
  const a = coreTitle(candidate);
  const b = coreTitle(wanted);
  if (a === b) return 120;
  if (a && b && (a.includes(b) || b.includes(a))) return 50;
  return -60;
}

function durationScore(candidate: number, wanted: number): number {
  if (wanted <= 0 || candidate <= 0) return 0;
  const delta = Math.abs(candidate - wanted);
  if (delta < 3) return 80;
  if (delta < 6) return 40;
  if (delta < 15) return 0;
  return -60;
}

function variantScore(candidate: TrackCandidate, wanted: WantedTrack): number {
  const candidateText = `${normalized(candidate.title)} ${normalized(candidate.artist)}`;
  const wantedText = `${normalized(wanted.title)} ${normalized(wanted.artist)}`;
  let result = 0;
  for (const marker of DISQUALIFYING_MARKERS) {
    if (candidateText.includes(marker) && !wantedText.includes(marker)) result -= 400;
  }
  for (const marker of SOFT_MARKERS) {
    const inCandidate = candidateText.includes(marker);
    const inWanted = wantedText.includes(marker);
    if (inCandidate && !inWanted) result -= 120;
    if (inWanted && !inCandidate) result -= 80;
  }
  return result;
}

/** Lowercased, diacritic-free, punctuation-free, single-spaced. */
export function normalized(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
}

/** The title without its edition suffixes — `(feat. X)`, `[Bonus Track]`,
 * `- Remastered 2011` — so the same song credited differently still matches. */
export function coreTitle(value: string): string {
  let result = "";
  let depth = 0;
  for (const character of value) {
    if (character === "(" || character === "[") depth += 1;
    else if (character === ")" || character === "]") depth = Math.max(0, depth - 1);
    else if (depth === 0) result += character;
  }
  const dash = result.indexOf(" - ");
  if (dash >= 0) result = result.slice(0, dash);
  return normalized(result);
}
